import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { BsChevronDown, BsChevronUp, BsPlus, BsTrash } from 'react-icons/bs';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '@/features/common/select/styles';
import { arrayMove } from '@dnd-kit/sortable';
import { DragHandle } from '@/features/common/sortable/SortableItem';

const FIELD_TYPE_OPTIONS = [
  { value: 'multi_select', label: 'Multi-Select Dropdown' },
  { value: 'number', label: 'Numeric' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'string', label: 'Text' },
];

const FIELD_TYPE_COLOR = {
  multi_select: 'blue',
  number: 'green',
  percentage: 'orange',
  string: 'gray',
};

const generateKey = (label, fieldId) => {
  const slugified = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_');
  return slugified || `field_${fieldId.slice(0, 8)}`;
};

const FormFieldCard = ({ field, isOpen, onToggle, modalityOptions, onUpdate, onDelete, draggable = true }) => {
  const [optionInput, setOptionInput] = useState('');

  const selectedFieldType = FIELD_TYPE_OPTIONS.find((o) => o.value === field.field_type) ?? null;

  const selectedModalities = (field.modality_ids || []).map((id) => modalityOptions.find((m) => m.value === id)).filter(Boolean);

  const handleLabelChange = (e) => {
    const label = e.target.value;
    onUpdate({ ...field, label, key: generateKey(label, field.id) });
  };

  const handleFieldTypeChange = (opt) => {
    onUpdate({ ...field, field_type: opt?.value ?? null });
  };

  const handleAddOption = () => {
    const trimmed = optionInput.trim();
    if (!trimmed) return;
    onUpdate({ ...field, options: [...(field.options || []), trimmed] });
    setOptionInput('');
  };

  const handleOptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const handleMoveOptionUp = (index) => {
    onUpdate({ ...field, options: arrayMove(field.options, index, index - 1) });
  };

  const handleMoveOptionDown = (index) => {
    onUpdate({ ...field, options: arrayMove(field.options, index, index + 1) });
  };

  const handleRemoveOption = (index) => {
    onUpdate({ ...field, options: field.options.filter((_, i) => i !== index) });
  };

  const handleRequiredChange = (e) => {
    onUpdate({ ...field, required: e.target.checked });
  };

  const handleModalitiesChange = (opts) => {
    onUpdate({ ...field, modality_ids: (opts || []).map((o) => o.value) });
  };

  const headerLabel = field.label || 'New Field';
  const headerBadgeColor = FIELD_TYPE_COLOR[field.field_type] ?? 'gray';
  const headerBadgeLabel = FIELD_TYPE_OPTIONS.find((o) => o.value === field.field_type)?.label ?? 'No type';
  const visibleModalities = selectedModalities.slice(0, 3);
  const hiddenModalities = selectedModalities.slice(3);

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="md" mb={3} bg="white">
      {/* Collapsed header */}
      <Flex
        onClick={onToggle}
        cursor="pointer"
        p={4}
        align="center"
        justify="space-between"
        _hover={{ bg: 'gray.50' }}
        borderBottomWidth={isOpen ? '1px' : '0'}
        borderBottomColor="gray.200"
        borderRadius={isOpen ? '0' : 'md'}
      >
        <HStack spacing={3} flex={1} minWidth={0} flexWrap="wrap">
          {draggable && (
            <Box as="span" onClick={(e) => e.stopPropagation()}>
              <DragHandle size="sm" />
            </Box>
          )}
          <Text fontWeight="semibold" noOfLines={1}>
            {headerLabel}
          </Text>
          {field.field_type && (
            <Badge colorScheme={headerBadgeColor} borderRadius="full" px={2} py={0.5} fontSize="xs">
              {headerBadgeLabel}
            </Badge>
          )}
          {visibleModalities.map((m) => (
            <Tag key={m.value} size="sm" colorScheme="purple" borderRadius="full">
              {m.label}
            </Tag>
          ))}
          {hiddenModalities.length > 0 && (
            <Popover placement="bottom-start" isLazy trigger="hover">
              <PopoverTrigger>
                <Tag size="sm" colorScheme="gray" borderRadius="full" cursor="default">
                  +{hiddenModalities.length} more
                </Tag>
              </PopoverTrigger>
              <PopoverContent minW="max-content">
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="semibold" fontSize="sm">
                  Modalities
                </PopoverHeader>
                <PopoverBody maxH="300px" overflowY="auto">
                  <VStack align="start" spacing={1}>
                    {hiddenModalities.map((m) => (
                      <Tag key={m.value} size="sm" colorScheme="purple" borderRadius="full">
                        {m.label}
                      </Tag>
                    ))}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </HStack>
        <HStack spacing={2} ml={3} flexShrink={0}>
          <Tooltip hasArrow label="Delete Field">
            <Box as="span">
              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="red"
                aria-label="delete field"
                icon={<BsTrash />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            </Box>
          </Tooltip>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={isOpen ? 'collapse' : 'expand'}
            icon={isOpen ? <BsChevronUp /> : <BsChevronDown />}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        </HStack>
      </Flex>

      {/* Expanded body */}
      <Collapse in={isOpen} animateOpacity>
        <Box p={4}>
          <VStack spacing={4} align="stretch">
            {/* Label */}
            <Flex align="center" gap={4}>
              <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
                Label
              </Text>
              <Input size="sm" focusBorderColor="brand.700" value={field.label} onChange={handleLabelChange} placeholder="e.g. Patient Population" />
            </Flex>

            {/* Field Type */}
            <Flex align="center" gap={4}>
              <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
                Field Type
              </Text>
              <Box flex={1}>
                <Select
                  size="sm"
                  chakraStyles={chakraStyles}
                  focusBorderColor="brand.700"
                  options={FIELD_TYPE_OPTIONS}
                  value={selectedFieldType}
                  onChange={handleFieldTypeChange}
                  placeholder="Select a field type"
                  isClearable={false}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 200 }) }}
                />
              </Box>
            </Flex>

            {/* Options — only for multi_select */}
            {field.field_type === 'multi_select' && (
              <Flex align="start" gap={4}>
                <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600" mt={2}>
                  Options
                </Text>
                <VStack align="stretch" flex={1} spacing={3}>
                  <HStack>
                    <Input
                      size="sm"
                      focusBorderColor="brand.700"
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyDown={handleOptionKeyDown}
                      placeholder="Add an option"
                    />
                    <IconButton
                      size="sm"
                      variant="cyan"
                      aria-label="add option"
                      icon={<BsPlus />}
                      isDisabled={!optionInput.trim()}
                      onClick={handleAddOption}
                    />
                  </HStack>
                  {(field.options || []).length > 0 && (
                    <Wrap spacing={2}>
                      {(field.options || []).map((option, index) => (
                        <WrapItem key={`${option}_${index}`}>
                          <Tag size="md" colorScheme="cyan" borderRadius="full">
                            <TagLabel>{option}</TagLabel>
                            <HStack spacing={0} ml={1}>
                              <IconButton
                                size="xs"
                                variant="ghost"
                                aria-label="move up"
                                icon={<BsChevronUp />}
                                isDisabled={index === 0}
                                onClick={() => handleMoveOptionUp(index)}
                                minW="auto"
                                h="auto"
                                p={0}
                              />
                              <IconButton
                                size="xs"
                                variant="ghost"
                                aria-label="move down"
                                icon={<BsChevronDown />}
                                isDisabled={index === (field.options || []).length - 1}
                                onClick={() => handleMoveOptionDown(index)}
                                minW="auto"
                                h="auto"
                                p={0}
                              />
                              <TagCloseButton onClick={() => handleRemoveOption(index)} />
                            </HStack>
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </VStack>
              </Flex>
            )}

            {/* Required */}
            <Flex align="center" gap={4}>
              <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
                Required
              </Text>
              <Checkbox isChecked={field.required} onChange={handleRequiredChange} colorScheme="purple" size="md" />
            </Flex>

            {/* Modalities */}
            <Flex align="center" gap={4}>
              <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
                Modalities
              </Text>
              <Box flex={1}>
                <Select
                  size="sm"
                  chakraStyles={chakraStyles}
                  focusBorderColor="brand.700"
                  options={modalityOptions}
                  value={selectedModalities}
                  onChange={handleModalitiesChange}
                  isMulti
                  isClearable
                  isSearchable
                  colorScheme="purple"
                  placeholder="Select modalities"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 200 }) }}
                />
              </Box>
            </Flex>
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};

FormFieldCard.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string,
    label: PropTypes.string,
    field_type: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
    required: PropTypes.bool,
    modality_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  modalityOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  draggable: PropTypes.bool,
};

export default FormFieldCard;
